import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SearchIcon from '@mui/icons-material/Search';

interface ListItem {
  id: string;
  label: string;
  secondary?: string;
}

interface DualListBoxProps {
  leftTitle: string;
  rightTitle: string;
  leftItems: ListItem[];
  rightItems: ListItem[];
  selectedLeft: string[];
  selectedRight: string[];
  onSelectLeft: (ids: string[]) => void;
  onSelectRight: (ids: string[]) => void;
  onMoveToRight: () => void;
  onMoveToLeft: () => void;
  isLoading?: boolean;
  disabled?: boolean;
}

/**
 * デュアルリストボックス（シャトルUI）コンポーネント
 * Ctrl+クリック: 選択に追加/解除
 * Shift+クリック: 範囲選択
 */
const DualListBox: React.FC<DualListBoxProps> = ({
  leftTitle,
  rightTitle,
  leftItems,
  rightItems,
  selectedLeft,
  selectedRight,
  onSelectLeft,
  onSelectRight,
  onMoveToRight,
  onMoveToLeft,
  isLoading = false,
  disabled = false,
}) => {
  const [leftFilter, setLeftFilter] = useState('');
  const [rightFilter, setRightFilter] = useState('');
  const lastClickedLeft = useRef<string | null>(null);
  const lastClickedRight = useRef<string | null>(null);

  // フィルタリング
  const filteredLeftItems = leftItems.filter(
    item =>
      item.label.toLowerCase().includes(leftFilter.toLowerCase()) ||
      item.secondary?.toLowerCase().includes(leftFilter.toLowerCase())
  );

  const filteredRightItems = rightItems.filter(
    item =>
      item.label.toLowerCase().includes(rightFilter.toLowerCase()) ||
      item.secondary?.toLowerCase().includes(rightFilter.toLowerCase())
  );

  // 選択ハンドラー
  const handleItemClick = useCallback(
    (
      e: React.MouseEvent,
      itemId: string,
      items: ListItem[],
      selected: string[],
      onSelect: (ids: string[]) => void,
      lastClickedRef: React.MutableRefObject<string | null>
    ) => {
      if (disabled) return;

      const itemIds = items.map(i => i.id);
      const clickedIndex = itemIds.indexOf(itemId);

      if (e.shiftKey && lastClickedRef.current) {
        // Shift+クリック: 範囲選択
        const lastIndex = itemIds.indexOf(lastClickedRef.current);
        if (lastIndex !== -1) {
          const start = Math.min(lastIndex, clickedIndex);
          const end = Math.max(lastIndex, clickedIndex);
          const rangeIds = itemIds.slice(start, end + 1);

          // 既存選択と範囲をマージ
          const newSelected = [...new Set([...selected, ...rangeIds])];
          onSelect(newSelected);
        }
      } else if (e.ctrlKey || e.metaKey) {
        // Ctrl+クリック: 選択に追加/解除
        if (selected.includes(itemId)) {
          onSelect(selected.filter(id => id !== itemId));
        } else {
          onSelect([...selected, itemId]);
        }
      } else {
        // 通常クリック: 単一選択
        onSelect([itemId]);
      }

      lastClickedRef.current = itemId;
    },
    [disabled]
  );

  const renderList = (
    title: string,
    items: ListItem[],
    selected: string[],
    onSelect: (ids: string[]) => void,
    filter: string,
    setFilter: (value: string) => void,
    lastClickedRef: React.MutableRefObject<string | null>
  ) => (
    <Paper
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: 400,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight="bold">
          {title} ({items.length}名)
        </Typography>
      </Box>

      <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder="検索..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <List
        dense
        sx={{
          flex: 1,
          overflow: 'auto',
          '& .MuiListItem-root': {
            cursor: disabled ? 'not-allowed' : 'pointer',
            userSelect: 'none',
            '&:hover': {
              bgcolor: disabled ? 'transparent' : 'action.hover',
            },
          },
        }}
      >
        {items.length === 0 ? (
          <ListItem>
            <ListItemText
              primary={
                <Typography variant="body2" color="text.secondary" align="center">
                  ユーザーがいません
                </Typography>
              }
            />
          </ListItem>
        ) : (
          items.map((item) => (
            <ListItem
              key={item.id}
              onClick={(e) =>
                handleItemClick(e, item.id, items, selected, onSelect, lastClickedRef)
              }
              sx={{
                bgcolor: selected.includes(item.id) ? 'primary.light' : 'transparent',
                color: selected.includes(item.id) ? 'primary.contrastText' : 'inherit',
                '&:hover': {
                  bgcolor: selected.includes(item.id) ? 'primary.main' : 'action.hover',
                },
              }}
            >
              <ListItemText
                primary={item.label}
                secondary={item.secondary}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: selected.includes(item.id) ? 'inherit' : 'text.primary',
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  color: selected.includes(item.id) ? 'inherit' : 'text.secondary',
                  sx: { opacity: selected.includes(item.id) ? 0.8 : 1 },
                }}
              />
            </ListItem>
          ))
        )}
      </List>

      <Box sx={{ p: 0.5, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          選択中: {selected.length}名
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {/* 左側リスト（グループ外） */}
      {renderList(
        leftTitle,
        filteredLeftItems,
        selectedLeft,
        onSelectLeft,
        leftFilter,
        setLeftFilter,
        lastClickedLeft
      )}

      {/* 中央ボタン */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Tooltip title="グループに追加" placement="top">
          <span>
            <IconButton
              onClick={onMoveToRight}
              disabled={disabled || selectedLeft.length === 0 || isLoading}
              color="primary"
              sx={{
                border: 1,
                borderColor: 'divider',
                '&:hover': { bgcolor: 'primary.light', color: 'white' },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="グループから削除" placement="bottom">
          <span>
            <IconButton
              onClick={onMoveToLeft}
              disabled={disabled || selectedRight.length === 0 || isLoading}
              color="primary"
              sx={{
                border: 1,
                borderColor: 'divider',
                '&:hover': { bgcolor: 'primary.light', color: 'white' },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* 右側リスト（グループ内） */}
      {renderList(
        rightTitle,
        filteredRightItems,
        selectedRight,
        onSelectRight,
        rightFilter,
        setRightFilter,
        lastClickedRight
      )}
    </Box>
  );
};

export default DualListBox;
